import { useEffect, useRef } from "react";
import { EPriceTabView } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { useRecoilValue } from "recoil";
import { useUser } from "MVVM/Hooks/useUser";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useUnlimitedOnlyPaywall } from "MVVM/Hooks/useUnlimitedOnlyPaywall";
import { useResolvedPaymentProvider } from "MVVM/Hooks/useResolvedPaymentProvider";
import { PromoSession } from "MVVM/State/promoSession";
import {
  UNLIMITED_PRICE_EXPERIMENT,
  getUnlimitedPriceOffer,
  unlimitedPriceOffer,
} from "MVVM/State/unlimitedPriceExperiment";

export const useUnlimitedPriceExperiment = (source: string) => {
  const { allstarUser } = useUser();
  const { unlimitedOnly } = useUnlimitedOnlyPaywall();
  const { provider } = useResolvedPaymentProvider();
  const internal = useIsInternalAccount();
  const promoSession = useRecoilValue(PromoSession);
  const promo = promoSession?.activeUpgradePagePromo;
  const offer = getUnlimitedPriceOffer({
    user: allstarUser?.user,
    unlimitedOnly,
    provider,
    internal,
    hasPromo:
      !promoSession.loaded ||
      !!(
        promo?.name ||
        promo?.pricing?.monthly?.platinum ||
        promo?.pricing?.annual?.platinum
      ),
    // Launch with the merged frontend; an explicit false remains the build-time kill switch.
    enabled: process.env.REACT_APP_UNLIMITED_PRICE_TEST !== "false",
  });
  const userId = allstarUser?.user?._id;
  const variant = offer?.variant;
  const exposure = useRef<string>();
  useEffect(() => {
    if (!variant || !userId) return;
    const key = `${userId}:${variant}:${source}`;
    if (exposure.current === key) return;
    exposure.current = key;
    const exposedOffer = unlimitedPriceOffer(variant);
    analytics.track("Unlimited Price Experiment Viewed", {
      experiment: UNLIMITED_PRICE_EXPERIMENT,
      price_variant: variant,
      source,
      provider: "stripe",
      account_id: userId,
      monthly_lookup_key: exposedOffer.stripeLookup[EPriceTabView.Monthly],
      annual_lookup_key: exposedOffer.stripeLookup[EPriceTabView.Annual],
    });
  }, [userId, variant, source]);
  return offer;
};
